import { ColumnType } from "antd/lib/table";
import { ReactNode } from "react";
import {
  AthleteCell,
  CollegeCell,
  RankCell,
  RankChangeCell,
  TextCell
} from "rivals/components/shared/Tables/Cells";
import { CareerType } from "rivals/shared/interfaces/Career";
import { CollegePlayer } from "rivals/shared/interfaces/List";
import { ENTRY_TYPES, EXIT_TYPES } from "rivals/shared/interfaces/Transfer";
import styles from "./index.module.scss";
import strings from "./strings";

const {
  emptyValue,
  entryIntent,
  inTransferPortal,
  tableColumnsTitles: { [CareerType.COLLEGE_PLAYER]: athleteTitles },
  transferredTo,
  unsigned,
  withdrew
} = strings;

const ordinalityColumn = {
  dataIndex: "ordinality",
  key: "ordinality",
  render: (ordinality: number, record: CollegePlayer): JSX.Element => (
    <div className={styles.ordinalityWrapper}>
      <RankCell href={record.collegePlayer.athleteUrl} rank={ordinality} />
    </div>
  ),
  title: athleteTitles.ordinality
};

const changeColumn = {
  dataIndex: "change",
  key: "change",
  render: (changeValue: number): JSX.Element => {
    return <RankChangeCell changeValue={changeValue} />;
  },
  title: athleteTitles.change
};

const athleteColumn = {
  dataIndex: "athlete",
  key: "athlete",
  render: (_: string, record: CollegePlayer): JSX.Element => {
    const { collegePlayer } = record;
    const {
      athleteUrl,
      collegePlayerProfileImage,
      firstName,
      lastName,
      transferStars
    } = collegePlayer;

    return (
      <AthleteCell
        careerType={CareerType.COLLEGE_PLAYER}
        firstName={firstName}
        href={athleteUrl}
        imageUrl={collegePlayerProfileImage}
        lastName={lastName}
        stars={transferStars}
      />
    );
  },
  title: athleteTitles.name
};

const positionColumn = {
  dataIndex: "position",
  key: "position",
  render: (_: string, record: CollegePlayer): JSX.Element => {
    const { collegePlayer } = record;

    return (
      <div className={styles.positionWrapper}>
        <TextCell
          alignment="left"
          fontSize="medium"
          text={
            collegePlayer?.transferPosition
              ? collegePlayer.transferPosition
              : emptyValue
          }
        />
        {!!record.collegePlayer?.exitCollegeLogo && (
          <div
            className={styles.logoOverlay}
            style={{
              backgroundImage: `url(${record.collegePlayer.exitCollegeLogo})`
            }}
          ></div>
        )}
      </div>
    );
  },
  title: athleteTitles.position
};

const originCollegeColumn = {
  dataIndex: "originCollege",
  key: "originCollege",
  render: (_: string, record: CollegePlayer): ReactNode => {
    const { collegePlayer } = record;

    return (
      <>
        {collegePlayer?.originCollegeName ? (
          <CollegeCell
            collegeColor={collegePlayer.originCollegeColor}
            collegeLogo={collegePlayer.originCollegeLogo}
            collegeName={collegePlayer.originCollegeName}
            fontSize={"medium"}
            inverted={false}
          />
        ) : (
          emptyValue
        )}
      </>
    );
  },
  title: athleteTitles.originCollege
};

const statusColumns = {
  dataIndex: "status",
  key: "status",
  render: (_: string, record: CollegePlayer): ReactNode => {
    const { collegePlayer } = record;

    if (
      collegePlayer?.exitType == EXIT_TYPES.TRANSFERRED &&
      collegePlayer?.exitCollegeName
    ) {
      return (
        <CollegeCell
          collegeColor={collegePlayer.exitCollegeColor}
          collegeLogo={collegePlayer.exitCollegeLogo}
          collegeName={collegePlayer.exitCollegeName}
          fontSize={"medium"}
          inverted
          overrideText={transferredTo}
        />
      );
    }

    let statusText;

    if (collegePlayer?.exitType === EXIT_TYPES.WITHDREW) {
      statusText = withdrew;
    } else if (collegePlayer?.exitType === EXIT_TYPES.NOT_SIGNED) {
      statusText = unsigned;
    } else if (collegePlayer?.entryType === ENTRY_TYPES.INTENT) {
      statusText = entryIntent;
    } else {
      statusText = inTransferPortal;
    }

    return <TextCell alignment="left" fontSize="medium" text={statusText} />;
  },
  title: athleteTitles.status
};

export const getTableColumns = (
  careerType: CareerType
): ColumnType<CollegePlayer>[] => {
  let columns: ColumnType<CollegePlayer>[] = [];

  switch (careerType) {
    case CareerType.COLLEGE_PLAYER:
      columns = [
        ordinalityColumn,
        changeColumn,
        athleteColumn,
        positionColumn,
        originCollegeColumn,
        statusColumns
      ];
      break;
    default:
      columns = [];
      break;
  }

  return columns;
};
